/**
 * NoTeamsPanel
 * ------------
 * Right-side tab content for the NI & Migration Map Views. Replaces the old
 * "Municipality" tab. Lists every job order / record that has no team assigned
 * yet as a compact card, with an inline "Assign team" control on each card.
 *
 * Clicking a card pans the map to the record's location (the host keeps the
 * current zoom) and highlights it. Clicking "Assign team" opens the host's
 * team-assignment popover (which also fills in the team composition).
 *
 * Page-agnostic: accessors fall back across NI and Migration record shapes.
 *
 * Props:
 *  - records:          array of records WITHOUT a team (host pre-filters)
 *  - statusColors:     STATUS_COLORS map from the host page (badge colors)
 *  - selectedRecordId: id of the currently-selected record (for highlight)
 *  - onCardClick:      (record) => void — host pans the map (no zoom change)
 *  - onAssignTeam:     (record, event) => void — opens the team popover anchored
 *                      to the clicked button (omit / canAssignTeam=false to hide)
 *  - canAssignTeam:    whether the user may assign teams
 *  - title:            optional heading (default "No Teams")
 */

function statusBucket(raw) {
  const u = (raw || "").toLowerCase();
  if (u.includes("cancel")) return "cancelled";
  if (u.includes("resched")) return "rescheduled";
  if (u.includes("complete")) return "completed";
  return "pending";
}

const BUCKET_LABEL = {
  pending: "Pending",
  completed: "Completed",
  rescheduled: "Rescheduled",
  cancelled: "Cancelled",
};

const recRef = (r) => r.jo_number || r.account_number || r.oldacctno || "—";
const recName = (r) => r.name || r.customer_full_name || "";
const recAddress = (r) => r.complete_address || r.address || "";
const recLocation = (r) =>
  [r.barangay, (r.municipality || "").replace(/^CITY OF\s+/i, "")]
    .filter(Boolean)
    .join(", ");
const recHasCoords = (r) =>
  r.latitude != null &&
  r.longitude != null &&
  !isNaN(r.latitude) &&
  !isNaN(r.longitude);

export default function NoTeamsPanel({
  records = [],
  statusColors = {},
  selectedRecordId = null,
  onCardClick,
  onAssignTeam,
  onNameClick,
  canAssignTeam = false,
  title = "No Teams",
}) {
  const bucketColors = {
    pending: statusColors.Pending || "#eab308",
    completed: statusColors["Active/Complete"] || statusColors.Completed || "#22c55e",
    rescheduled: statusColors.Rescheduled || "#f97316",
    cancelled: statusColors.Cancelled || "#ef4444",
  };

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-3">
        <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{title}</h3>
        <span className="text-[10px] font-bold text-gray-500 tabular-nums bg-gray-100 rounded-full px-1.5 min-w-[18px] text-center leading-tight">
          {records.length}
        </span>
      </div>

      {records.length === 0 ? (
        <p className="text-xs text-gray-400">All job orders have a team assigned.</p>
      ) : (
        <div className="space-y-1.5">
          {records.map((r) => {
            const bucket = statusBucket(r.status || r.jo_status);
            const badgeColor = bucketColors[bucket];
            const isSelected = selectedRecordId != null && r.id === selectedRecordId;
            return (
              <div
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => onCardClick?.(r)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onCardClick?.(r);
                  }
                }}
                title={recHasCoords(r) ? "Locate on map" : "No saved coordinates"}
                className={`w-full text-left rounded-md border bg-white border-l-[3px] px-2 py-1.5 transition-colors cursor-pointer ${
                  isSelected ? "ring-1 ring-blue-400 bg-blue-50 border-gray-200" : "border-gray-200 hover:bg-gray-50"
                }`}
                style={{ borderLeftColor: badgeColor }}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-gray-800 truncate flex-1 min-w-0">
                    {recRef(r)}
                  </span>
                  <span
                    className="text-[8.5px] font-bold uppercase tracking-wide px-1 py-[1px] rounded text-white flex-shrink-0"
                    style={{ backgroundColor: badgeColor }}
                  >
                    {BUCKET_LABEL[bucket]}
                  </span>
                </div>
                {recName(r) && (
                  onNameClick ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onNameClick(r); }}
                      title="Locate subscriber by address"
                      className="block max-w-full truncate text-left text-[10px] font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {recName(r)}
                    </button>
                  ) : (
                    <div className="text-[10px] text-gray-700 truncate">{recName(r)}</div>
                  )
                )}
                {recLocation(r) && (
                  <div className="text-[9.5px] text-gray-500 truncate">{recLocation(r)}</div>
                )}
                {recAddress(r) && (
                  <div className="text-[9.5px] text-gray-400 truncate">{recAddress(r)}</div>
                )}

                {canAssignTeam && onAssignTeam ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignTeam(r, e);
                    }}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded px-1.5 py-0.5 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Assign team
                  </button>
                ) : (
                  <div className="mt-1 text-[9.5px] italic text-gray-400">No team assigned</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
